import { useState, useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import { sanitizeHTML } from '../utils/sanitization'

export const CATEGORY_STYLES = {
  feature: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-300',
    icon: 'bg-blue-500'
  },
  improvement: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-300',
    icon: 'bg-green-500'
  },
  fix: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-700 dark:text-orange-300',
    icon: 'bg-orange-500'
  },
  announcement: {
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-700 dark:text-purple-300',
    icon: 'bg-purple-500'
  }
}

export const TOPIC_STYLES = {
  learn: {
    bg: 'bg-cyan-100 dark:bg-cyan-900/30',
    text: 'text-cyan-700 dark:text-cyan-300'
  },
  understand: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-300'
  },
  compete: {
    bg: 'bg-rose-100 dark:bg-rose-900/30',
    text: 'text-rose-700 dark:text-rose-300'
  }
}

export const getRelativeDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const diffDays = Math.round((startOfToday - startOfDate) / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'today'
  if (diffDays === 1) return 'yesterday'
  if (diffDays >= 2 && diffDays <= 6) return `${diffDays} days ago`
  if (diffDays >= 7 && diffDays <= 13) return 'last week'
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export const getCategoryStyle = (category) => {
  return CATEGORY_STYLES[category] || CATEGORY_STYLES.announcement
}

const FREQUENCY_OPTIONS = [
  { value: 'every_login', label: 'Every login' },
  { value: 'daily', label: 'Once a day' },
  { value: 'weekly', label: 'Once a week' }
]

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 80 : -80, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -80 : 80, opacity: 0 })
}

const WhatsNewModal = ({
  isOpen,
  onClose,
  announcements = [],
  onMarkAsRead,
  userFrequency = 'every_login',
  onFrequencyChange
}) => {
  const modalRef = useRef(null)
  const closeButtonRef = useRef(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const readIds = useRef(new Set())
  const [direction, setDirection] = useState(0)

  // Reset carousel when modal opens with new announcements
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0)
      readIds.current.clear()
    }
  }, [isOpen])

  const markCurrentAsRead = useCallback(() => {
    if (!announcements[currentIndex]) return
    const id = announcements[currentIndex].id
    if (!readIds.current.has(id)) {
      readIds.current.add(id)
      onMarkAsRead?.([id])
    }
  }, [announcements, currentIndex, onMarkAsRead])

  const goNext = useCallback(() => {
    markCurrentAsRead()
    if (currentIndex < announcements.length - 1) {
      setDirection(1)
      setCurrentIndex((i) => i + 1)
    } else {
      onClose()
    }
  }, [currentIndex, announcements.length, markCurrentAsRead, onClose])

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      markCurrentAsRead()
      setDirection(-1)
      setCurrentIndex((i) => i - 1)
    }
  }, [currentIndex, markCurrentAsRead])

  useEffect(() => {
    if (!isOpen) return

    const timer = setTimeout(() => {
      closeButtonRef.current?.focus()
    }, 100)

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        markCurrentAsRead()
        onClose()
      } else if (e.key === 'ArrowRight') {
        goNext()
      } else if (e.key === 'ArrowLeft') {
        goPrev()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose, goNext, goPrev, markCurrentAsRead])

  const handleClose = useCallback(() => {
    markCurrentAsRead()
    onClose()
  }, [markCurrentAsRead, onClose])

  const current = announcements[currentIndex]
  const isMultiple = announcements.length > 1
  const isLast = currentIndex === announcements.length - 1

  return (
    <AnimatePresence>
      {isOpen && announcements.length > 0 && current && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleClose}
            aria-hidden="true"
          />

          {/* Modal */}
          <motion.div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="whats-new-title"
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-6 h-6 text-white" />
                  <h2 id="whats-new-title" className="text-xl font-bold text-white">
                    What's New
                  </h2>
                  {isMultiple && (
                    <span className="text-white/70 text-sm">
                      {currentIndex + 1} / {announcements.length}
                    </span>
                  )}
                </div>
                <button
                  ref={closeButtonRef}
                  onClick={handleClose}
                  className="text-white/80 hover:text-white transition-colors rounded-lg p-1 hover:bg-white/10"
                  aria-label="Close what's new dialog"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Body with carousel */}
            <div className="px-6 py-5 min-h-[200px]">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={current.id}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.2 }}
                >
                  <SingleAnnouncementView announcement={current} />
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Navigation dots + arrows */}
            {isMultiple && (
              <div className="flex items-center justify-center gap-3 px-6 pb-2">
                <button
                  onClick={goPrev}
                  disabled={currentIndex === 0}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed rounded transition-colors"
                  aria-label="Previous announcement"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-1.5">
                  {announcements.map((a, i) => (
                    <button
                      key={a.id}
                      onClick={() => {
                        if (i === currentIndex) return
                        markCurrentAsRead()
                        setDirection(i > currentIndex ? 1 : -1)
                        setCurrentIndex(i)
                      }}
                      className={`w-2 h-2 rounded-full transition-all ${
                        i === currentIndex
                          ? 'bg-indigo-600 dark:bg-indigo-400 w-4'
                          : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400'
                      }`}
                      aria-label={`Go to announcement ${i + 1}`}
                    />
                  ))}
                </div>
                <button
                  onClick={goNext}
                  disabled={isLast}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed rounded transition-colors"
                  aria-label="Next announcement"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Link
                  to="/whats-new"
                  onClick={handleClose}
                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline whitespace-nowrap"
                >
                  View all
                </Link>
                {onFrequencyChange && (
                  <select
                    value={userFrequency}
                    onChange={(e) => onFrequencyChange(e.target.value)}
                    aria-label="Announcement notification frequency"
                    className="text-xs border border-gray-200 dark:border-gray-600 rounded px-1.5 py-1 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    {FREQUENCY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <button
                onClick={goNext}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg px-6 py-2.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
              >
                {isLast || !isMultiple ? 'Got it!' : 'Next'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export const SingleAnnouncementView = ({ announcement }) => {
  const style = getCategoryStyle(announcement.category)

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div
          className={`w-10 h-10 rounded-full ${style.icon} flex items-center justify-center text-white font-bold text-sm shrink-0`}
        >
          {(announcement.category || 'A').charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
            {announcement.title}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            <span
              className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}
            >
              {announcement.category}
            </span>
            {announcement.topic && TOPIC_STYLES[announcement.topic] && (
              <span
                className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${TOPIC_STYLES[announcement.topic].bg} ${TOPIC_STYLES[announcement.topic].text}`}
              >
                {announcement.topic}
              </span>
            )}
            {announcement.publishedAt && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {getRelativeDate(announcement.publishedAt)}
              </span>
            )}
          </div>
        </div>
      </div>
      {announcement.content && (
        <div
          className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300"
          dangerouslySetInnerHTML={{ __html: sanitizeHTML(announcement.content) }}
        />
      )}
    </div>
  )
}

SingleAnnouncementView.propTypes = {
  announcement: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string.isRequired,
    summary: PropTypes.string,
    content: PropTypes.string,
    icon: PropTypes.string,
    category: PropTypes.string,
    topic: PropTypes.string,
    publishedAt: PropTypes.string
  }).isRequired
}

export const AnnouncementCard = ({ announcement }) => {
  const style = getCategoryStyle(announcement.category)

  return (
    <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750 hover:border-gray-300 dark:hover:border-gray-600 transition-colors">
      <div className="flex gap-3">
        <div
          className={`w-9 h-9 rounded-full ${style.icon} flex items-center justify-center text-white font-bold text-xs shrink-0 mt-0.5`}
        >
          {(announcement.category || 'A').charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 leading-snug">
            {announcement.title}
          </h3>
          {announcement.summary && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
              {announcement.summary}
            </p>
          )}
          <div className="flex items-center gap-2 mt-2">
            <span
              className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}
            >
              {announcement.category}
            </span>
            {announcement.topic && TOPIC_STYLES[announcement.topic] && (
              <span
                className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${TOPIC_STYLES[announcement.topic].bg} ${TOPIC_STYLES[announcement.topic].text}`}
              >
                {announcement.topic}
              </span>
            )}
            {announcement.publishedAt && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {getRelativeDate(announcement.publishedAt)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

AnnouncementCard.propTypes = {
  announcement: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string.isRequired,
    summary: PropTypes.string,
    content: PropTypes.string,
    icon: PropTypes.string,
    category: PropTypes.string,
    topic: PropTypes.string,
    publishedAt: PropTypes.string
  }).isRequired
}

WhatsNewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  announcements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      summary: PropTypes.string,
      content: PropTypes.string,
      icon: PropTypes.string,
      category: PropTypes.string,
      topic: PropTypes.string,
      publishedAt: PropTypes.string
    })
  ),
  onMarkAsRead: PropTypes.func,
  userFrequency: PropTypes.string,
  onFrequencyChange: PropTypes.func
}

export default WhatsNewModal
